import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import {
  FiUsers,
  FiUserCheck,
  FiUserPlus,
  FiUserX,
  FiRepeat,
  FiShoppingBag,
  FiDollarSign,
  FiClock,
  FiChevronRight,
  FiX,
  FiUser,
} from "react-icons/fi";

/* ─── Yardımcılar ─── */
const fmt = (n) => Number(n || 0).toLocaleString("tr-TR");
const fmtMoney = (n, sym = "₺") =>
  sym +
  parseFloat(n || 0).toLocaleString("tr-TR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const fmtDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" });
};
const daysSince = (d) => {
  if (!d) return null;
  return Math.floor((Date.now() - new Date(d)) / (1000 * 60 * 60 * 24));
};

/* ─── Bar ─── */
const Bar = ({ value, max, color = "#378ADD" }) => (
  <div style={{ flex: 1, height: 6, background: "var(--bs-gray-200,#e9ecef)", borderRadius: 3, overflow: "hidden" }}>
    <div style={{ width: `${Math.round((value / Math.max(max, 1)) * 100)}%`, height: "100%", background: color, borderRadius: 3, transition: "width .4s" }} />
  </div>
);

/* ─── Metrik kutusu ─── */
const MetricMini = ({ label, value, color }) => (
  <div style={{ flex: 1, background: "var(--bs-gray-100,#f8f9fa)", borderRadius: 8, padding: "10px 12px" }}>
    <div style={{ fontSize: 11, color: "#6c757d" }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 500, marginTop: 2, color: color || "inherit" }}>{value}</div>
  </div>
);

/* ─── Misafir / Kayıtlı badge ─── */
const UserTypeBadge = ({ isGuest }) =>
  isGuest ? (
    <span className="badge bg-warning text-dark" style={{ fontSize: 10 }}>Misafir</span>
  ) : (
    <span className="badge bg-success" style={{ fontSize: 10 }}>Üye</span>
  );

/* ─── Uyarı: endpoint yok ─── */
const NoData = ({ label = "Veri yükleniyor..." }) => (
  <div className="text-center py-4 text-muted" style={{ fontSize: 13 }}>
    <FiUsers style={{ fontSize: 24, marginBottom: 8, display: "block", margin: "0 auto 8px" }} />
    {label}
  </div>
);

/* ══════════════════════════════════════════
   ANA BİLEŞEN
══════════════════════════════════════════ */
const CustomerAnalytics = () => {
  const [stats, setStats] = useState(null);
  const [report, setReport] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);
  const [openPanel, setOpenPanel] = useState(null);

  const chartRefMonthly = useRef(null);
  const chartRefRatio = useRef(null);
  const chartInstanceMonthly = useRef(null);
  const chartInstanceRatio = useRef(null);

  /* ── /api/customers/stats ── */
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/customers/stats`)
      .then((r) => setStats(r.data))
      .catch(() => setStats(null))
      .finally(() => setLoadingStats(false));
  }, []);

  /* ✅ FIX: /api/customers/report — ilk render'da OTOMATIK çek ── */
  useEffect(() => {
    if (loadingStats) return;

    setLoadingReport(true);
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/customers/report`)
      .then((r) => {
        console.log("✅ Report yüklendi:", r.data);
        setReport(r.data);
      })
      .catch((err) => {
        console.error("❌ Report yüklenemedi:", err);
        setReport(null);
      })
      .finally(() => setLoadingReport(false));
  }, [loadingStats]);

  const handleOpen = (id) => {
    if (openPanel === id) { setOpenPanel(null); return; }
    setOpenPanel(id);
  };

  // ✅ Para bilgisini report'tan al
  const sym = report?.currencySymbol || "₺";
  const currency = report?.currency || "TRY";

  /* ── Chart.js ── */
  const ensureChart = () =>
    new Promise((res) => {
      if (window.Chart) return res();
      const s = document.createElement("script");
      s.src = "https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.js";
      s.onload = res;
      document.head.appendChild(s);
    });

  /* Line: aylık yeni kayıt */
  useEffect(() => {
    if (openPanel !== "newcust" || !chartRefMonthly.current || !stats) return;
    let alive = true;
    const build = async () => {
      await ensureChart();
      if (!alive || !chartRefMonthly.current) return;
      if (chartInstanceMonthly.current) { chartInstanceMonthly.current.destroy(); chartInstanceMonthly.current = null; }
      const data = stats.monthlyNew || Array(12).fill(0);
      const now = new Date();
      const labels = Array.from({ length: 12 }, (_, i) => {
        const d = new Date(now.getFullYear(), now.getMonth() - 11 + i, 1);
        return d.toLocaleDateString("tr-TR", { month: "short" });
      });
      chartInstanceMonthly.current = new window.Chart(chartRefMonthly.current, {
        type: "line",
        data: {
          labels,
          datasets: [{ label: "Yeni müşteri", data, borderColor: "#1D9E75", backgroundColor: "rgba(29,158,117,0.08)", borderWidth: 2, pointRadius: 3, fill: true, tension: 0.35 }],
        },
        options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { grid: { display: false }, ticks: { font: { size: 11 } } }, y: { grid: { color: "rgba(128,128,128,0.1)" }, ticks: { font: { size: 11 }, stepSize: 1 } } } },
      });
    };
    build();
    return () => { alive = false; if (chartInstanceMonthly.current) { chartInstanceMonthly.current.destroy(); chartInstanceMonthly.current = null; } };
  }, [openPanel, stats]);

  /* Donut: yeni / tekrar müşteri */
  useEffect(() => {
    if (openPanel !== "ratio" || !chartRefRatio.current || !report) return;
    let alive = true;
    const build = async () => {
      await ensureChart();
      if (!alive || !chartRefRatio.current) return;
      if (chartInstanceRatio.current) { chartInstanceRatio.current.destroy(); chartInstanceRatio.current = null; }
      chartInstanceRatio.current = new window.Chart(chartRefRatio.current, {
        type: "doughnut",
        data: {
          labels: ["Yeni müşteri (1 sipariş)", "Tekrar müşteri (2+ sipariş)"],
          datasets: [{ data: [report.summary.newCustomers, report.summary.returningCustomers], backgroundColor: ["#378ADD", "#1D9E75"], borderWidth: 0 }],
        },
        options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: true, position: "bottom", labels: { font: { size: 11 }, padding: 12 } } } },
      });
    };
    build();
    return () => { alive = false; if (chartInstanceRatio.current) { chartInstanceRatio.current.destroy(); chartInstanceRatio.current = null; } };
  }, [openPanel, report]);

  /* ── Kart tanımları ── */
  const cards = [
    {
      id: "total",
      icon: <FiUsers />,
      iconClass: "text-primary bg-soft-primary border-soft-primary",
      label: "Toplam müşteri",
      value: loadingStats ? "..." : fmt(stats?.total),
      sub: `${fmt(stats?.registeredCount)} üye · ${fmt(stats?.guestCount)} misafir`,
    },
    {
      id: "avgcart",
      icon: <FiDollarSign />,
      iconClass: "text-success bg-soft-success border-soft-success",
      label: "Ortalama sepet tutarı",
      // ✅ FIX: para sembolü kullan (sym parametresi)
      value: loadingStats || loadingReport ? "..." : (report ? fmtMoney(report.summary?.avgCartValue, sym) : "—"),
      sub: "satın alınan sepetler",
    },
    {
      id: "frequent",
      icon: <FiShoppingBag />,
      iconClass: "text-info bg-soft-info border-soft-info",
      label: "Sık alışveriş yapanlar",
      value: "Listele",
      sub: "en aktif müşteriler",
    },
    {
      id: "highvalue",
      icon: <FiDollarSign />,
      iconClass: "text-warning bg-soft-warning border-soft-warning",
      label: "Yüksek sepet ortalamalı",
      value: "Listele",
      sub: "toplam harcamaya göre",
    },
    {
      id: "dormant",
      icon: <FiUserX />,
      iconClass: "text-danger bg-soft-danger border-soft-danger",
      label: "Uzun süredir alışveriş yok",
      value: "Listele",
      sub: "90+ gün inaktif",
    },
    {
      id: "recent",
      icon: <FiClock />,
      iconClass: "text-primary bg-soft-primary border-soft-primary",
      label: "Son alışveriş tarihi",
      value: "Listele",
      sub: "son 30 günde alışveriş",
    },
    {
      id: "guest",
      icon: <FiUser />,
      iconClass: "text-secondary bg-soft-secondary border-soft-secondary",
      label: "Üyeliksiz alışveriş",
      value: loadingStats ? "..." : fmt(stats?.guestCount),
      sub: "misafir kullanıcılar",
    },
    {
      id: "ratio",
      icon: <FiRepeat />,
      iconClass: "text-success bg-soft-success border-soft-success",
      label: "Yeni / Tekrar müşteri",
      value: loadingReport ? "..." : (report ? `%${report.summary.returningRate}` : "—"),
      sub: "tekrar alışveriş oranı",
    },
    {
      id: "newcust",
      icon: <FiUserPlus />,
      iconClass: "text-info bg-soft-info border-soft-info",
      label: "Yeni kayıt trendi",
      value: loadingStats ? "..." : fmt(stats?.newThisMonth),
      sub: "bu ay yeni kayıt",
    },
    {
      id: "activecust",
      icon: <FiUserCheck />,
      iconClass: "text-success bg-soft-success border-soft-success",
      label: "Aktif müşteri",
      value: loadingStats ? "..." : fmt(stats?.active),
      sub: "son 30 günde aktif",
    },
  ];

  /* ── Panel içerikleri ── */
  const renderPanel = (id) => {
    const isLoading = loadingReport && !report;

    if (id === "total") {
      if (!stats) return <NoData label="Veri alınamadı." />;
      return (
        <>
          <div className="row g-2 mb-3">
            <div className="col-3"><MetricMini label="Toplam" value={fmt(stats.total)} /></div>
            <div className="col-3"><MetricMini label="Kayıtlı üye" value={fmt(stats.registeredCount)} color="#1D9E75" /></div>
            <div className="col-3"><MetricMini label="Misafir" value={fmt(stats.guestCount)} color="#BA7517" /></div>
            <div className="col-3"><MetricMini label="Bu ay yeni" value={fmt(stats.newThisMonth)} color="#185FA5" /></div>
          </div>
          {[
            { label: "Kayıtlı üye", value: stats.registeredCount, color: "#1D9E75" },
            { label: "Misafir", value: stats.guestCount, color: "#BA7517" },
          ].map((r) => (
            <div key={r.label} className="d-flex align-items-center gap-2 mb-2" style={{ fontSize: 13 }}>
              <span style={{ width: 110, flexShrink: 0, color: "#6c757d" }}>{r.label}</span>
              <Bar value={r.value} max={stats.total} color={r.color} />
              <span style={{ width: 55, textAlign: "right", fontWeight: 500 }}>{fmt(r.value)}</span>
            </div>
          ))}
        </>
      );
    }

    if (id === "avgcart") {
      if (isLoading) return <NoData label="Rapor yükleniyor..." />;
      if (!report) return <NoData label="Rapor verisi alınamadı." />;
      return (
        <div className="row g-2">
          <div className="col-4"><MetricMini label="Ortalama sepet" value={fmtMoney(report.summary.avgCartValue, sym)} /></div>
          <div className="col-4"><MetricMini label="Yeni müşteri" value={fmt(report.summary.newCustomers)} /></div>
          <div className="col-4"><MetricMini label="Tekrar müşteri" value={fmt(report.summary.returningCustomers)} /></div>
        </div>
      );
    }

    if (id === "frequent") {
      if (isLoading) return <NoData label="Rapor yükleniyor..." />;
      if (!report) return <NoData label="Rapor verisi alınamadı." />;
      const data = report.frequentBuyers || [];
      if (!data.length) return <NoData label="Veri bulunamadı." />;
      const maxCart = Math.max(...data.map((d) => d.orderCount), 1);
      return (
        <>
          <p className="text-muted small mb-3">Satın alınan sipariş sayısına göre en aktif 10 müşteri</p>
          <table className="table table-hover table-sm mb-0">
            <thead className="table-light">
              <tr><th>Müşteri</th><th>Tip</th><th>Sipariş</th><th style={{ width: 120 }}>Oran</th><th>Son aktif</th></tr>
            </thead>
            <tbody>
              {data.map((c, i) => (
                <tr key={i}>
                  <td>
                    <strong>{c.name}</strong>
                    <br /><small className="text-muted">{c.email}</small>
                  </td>
                  <td><UserTypeBadge isGuest={c.isGuest} /></td>
                  <td>{fmt(c.orderCount)}</td>
                  <td><Bar value={c.orderCount} max={maxCart} color="#378ADD" /></td>
                  <td className="text-muted" style={{ fontSize: 12 }}>{fmtDate(c.lastActivity)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      );
    }

    if (id === "highvalue") {
      if (isLoading) return <NoData label="Rapor yükleniyor..." />;
      if (!report) return <NoData label="Rapor verisi alınamadı." />;
      const data = report.highValueCustomers || [];
      if (!data.length) return <NoData label="Veri bulunamadı." />;
      return (
        <>
          <p className="text-muted small mb-3">Toplam harcamaya göre en yüksek 10 müşteri ({currency})</p>
          <table className="table table-hover table-sm mb-0">
            <thead className="table-light">
              <tr><th>#</th><th>Müşteri</th><th>Tip</th><th>Toplam harcama</th><th>Sipariş</th></tr>
            </thead>
            <tbody>
              {data.map((c, i) => (
                <tr key={i}>
                  <td className="text-muted">{i + 1}</td>
                  <td>
                    <strong>{c.name}</strong>
                    <br /><small className="text-muted">{c.email}</small>
                  </td>
                  <td><UserTypeBadge isGuest={c.isGuest} /></td>
                  <td><strong className="text-success">{fmtMoney(c.totalSpent, sym)}</strong></td>
                  <td>{fmt(c.orderCount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      );
    }

    if (id === "dormant") {
      if (isLoading) return <NoData label="Rapor yükleniyor..." />;
      if (!report) return <NoData label="Rapor verisi alınamadı." />;
      const data = report.dormantCustomers || [];
      if (!data.length) return <NoData label="90+ gün inaktif müşteri bulunamadı." />;
      return (
        <>
          <p className="text-muted small mb-3">Son aktivitesi 90 günden eski müşteriler — yeniden kazanma fırsatı</p>
          <table className="table table-hover table-sm mb-0">
            <thead className="table-light">
              <tr><th>Müşteri</th><th>Tip</th><th>Son alışveriş</th><th>İnaktif gün</th></tr>
            </thead>
            <tbody>
              {data.map((c, i) => {
                const days = daysSince(c.lastActivity);
                return (
                  <tr key={i}>
                    <td>
                      <strong>{c.name}</strong>
                      <br /><small className="text-muted">{c.email}</small>
                    </td>
                    <td><UserTypeBadge isGuest={c.isGuest} /></td>
                    <td className="text-muted" style={{ fontSize: 12 }}>{fmtDate(c.lastActivity)}</td>
                    <td>
                      <span className={`badge ${days > 180 ? "bg-danger" : days > 90 ? "bg-warning text-dark" : "bg-secondary"}`}>
                        {days ?? "?"} gün
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      );
    }

    if (id === "recent") {
      if (isLoading) return <NoData label="Rapor yükleniyor..." />;
      if (!report) return <NoData label="Rapor verisi alınamadı." />;
      const data = report.recentShoppers || [];
      if (!data.length) return <NoData label="Son 30 gün alışveriş bulunamadı." />;
      return (
        <>
          <p className="text-muted small mb-3">Son 30 gün içinde alışveriş yapmış müşteriler</p>
          <table className="table table-hover table-sm mb-0">
            <thead className="table-light">
              <tr><th>Müşteri</th><th>Tip</th><th>Son alışveriş</th><th>İşlem sayısı</th></tr>
            </thead>
            <tbody>
              {data.map((c, i) => (
                <tr key={i}>
                  <td>
                    <strong>{c.name}</strong>
                    <br /><small className="text-muted">{c.email}</small>
                  </td>
                  <td><UserTypeBadge isGuest={c.isGuest} /></td>
                  <td style={{ fontSize: 12 }}>{fmtDate(c.lastActivity)}</td>
                  <td>{fmt(c.orderCount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      );
    }

    if (id === "guest") {
      if (isLoading) return <NoData label="Rapor yükleniyor..." />;
      if (!report) return <NoData label="Rapor verisi alınamadı." />;
      const data = report.guestShoppers || [];
      if (!data.length) return <NoData label="Misafir alışverişçi bulunamadı." />;
      return (
        <table className="table table-hover table-sm mb-0">
          <thead className="table-light">
            <tr><th>E-posta / ID</th><th>İşlem</th><th>Son aktivite</th></tr>
          </thead>
          <tbody>
            {data.map((c, i) => (
              <tr key={i}>
                <td>
                  <strong>{c.name}</strong>
                  <br /><small className="text-muted">{c.email}</small>
                </td>
                <td>{fmt(c.orderCount)}</td>
                <td className="text-muted" style={{ fontSize: 12 }}>{fmtDate(c.lastActivity)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    if (id === "ratio") {
      if (isLoading) return <NoData label="Rapor yükleniyor..." />;
      if (!report) return <NoData label="Rapor verisi alınamadı." />;
      const { newCustomers, returningCustomers, returningRate } = report.summary;
      return (
        <>
          <div className="row g-2 mb-3">
            <div className="col-4"><MetricMini label="Yeni müşteri" value={fmt(newCustomers)} color="#378ADD" /></div>
            <div className="col-4"><MetricMini label="Tekrar müşteri" value={fmt(returningCustomers)} color="#1D9E75" /></div>
            <div className="col-4"><MetricMini label="Tekrar oranı" value={`%${returningRate}`} /></div>
          </div>
          <div style={{ position: "relative", width: "100%", height: 220 }}>
            <canvas ref={chartRefRatio} />
          </div>
        </>
      );
    }

    if (id === "newcust") {
      if (!stats) return <NoData label="İstatistik alınamadı." />;
      return (
        <>
          <p className="text-muted small mb-3">Son 12 aylık yeni müşteri kaydı</p>
          <div style={{ position: "relative", width: "100%", height: 200 }}>
            <canvas ref={chartRefMonthly} />
          </div>
        </>
      );
    }

    if (id === "activecust") {
      if (!stats) return <NoData label="İstatistik alınamadı." />;
      return (
        <div className="row g-2">
          <div className="col-4"><MetricMini label="Son 30 gün aktif" value={fmt(stats.active)} color="#1D9E75" /></div>
          <div className="col-4"><MetricMini label="Toplam" value={fmt(stats.total)} /></div>
          <div className="col-4"><MetricMini label="Aktivasyon" value={stats.total > 0 ? `%${Math.round(stats.active / stats.total * 100)}` : "—"} /></div>
        </div>
      );
    }

    return null;
  };

  /* ══ RENDER ══ */
  return (
    <div>
      {/* Özet header */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h6 className="text-muted text-uppercase fw-semibold mb-0" style={{ fontSize: 11, letterSpacing: "0.08em" }}>
          Müşteri Analizi
        </h6>
        {(loadingStats || loadingReport) && (
          <div className="spinner-border spinner-border-sm text-secondary" style={{ width: 14, height: 14 }} />
        )}
      </div>

      {/* Kart grid */}
      <div className="row g-3 mb-2">
        {cards.map((card) => {
          const isOpen = openPanel === card.id;
          return (
            <div key={card.id} className="col-6 col-md-3 col-xl-2">
              <div
                className={`card h-100 ${isOpen ? "border-primary" : ""}`}
                style={{ cursor: "pointer", transition: "border-color .15s" }}
                onClick={() => handleOpen(card.id)}
              >
                <div className="card-body p-3">
                  <div className="d-flex align-items-start justify-content-between mb-2">
                    <div className={`avatar-text avatar-sm ${card.iconClass}`} style={{ fontSize: 14 }}>
                      {card.icon}
                    </div>
                    <FiChevronRight
                      style={{ fontSize: 14, color: "#adb5bd", transform: isOpen ? "rotate(90deg)" : "none", transition: "transform .2s" }}
                    />
                  </div>
                  <div className="text-muted mb-1" style={{ fontSize: 11 }}>{card.label}</div>
                  <div className="fw-semibold" style={{ fontSize: 18, lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {card.value}
                  </div>
                  <div className="text-muted mt-1" style={{ fontSize: 11 }}>{card.sub}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Açılan detay paneli */}
      {openPanel && (
        <div className="card mb-2">
          <div className="card-body">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h6 className="mb-0 fw-semibold">
                {cards.find((c) => c.id === openPanel)?.label}
              </h6>
              <button className="btn btn-sm btn-light" onClick={() => setOpenPanel(null)}>
                <FiX style={{ fontSize: 14 }} />
              </button>
            </div>
            {renderPanel(openPanel)}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerAnalytics;